import { ItemListProps } from '@engyalo/delivery-ui-components/lib/ui/components/ItemList'
import { DefaultProps } from '../../../interfaces'
import deepMerge from '../../../utils/deepMerge'
import {
  CinnamonActionNames,
  CinnamonPaths,
  DefaultModalContainerNames,
  Referrers,
  TemplateActionNames,
  TextLibrary,
} from '../defaultConstants'
import { CounterDefaultArgs } from './childProps/Counter'
import {
  ItemCardProductRecommended,
  ItemCardCatalog,
  ItemCardPrefillCart,
  ItemCardLinkedProducts,
  ItemCardSearch,
  ItemCardFreegoodsQuantitySelection,
} from './ItemCard'
import { ItemField } from '@engyalo/delivery-ui-components/lib/interfaces/item'

export const ItemListDefault: DefaultProps<ItemListProps> = {}

export const ItemListSearchResults = deepMerge(ItemListDefault, {
  layout: 'condensed',
  items: '{{catalog.search.items}}' as any,
  status: '{{catalog.search.status}}' as any,
  itemCard: {
    ...ItemCardSearch,
    image: {
      ...ItemCardSearch.image,
      dimensions: '{{globals.imageConfig.imageSizes.searchImage}}' as any,
    },
  },
  heading: {
    value: TextLibrary.SEARCH_RESULTS_TITLE,
    variant: 'h2',
    styles: {
      marginTop: '-8px',
    },
  },
  emptyMessage: {
    icon: {
      name: 'MdSearch',
    },
    title: TextLibrary.SEARCH_EMPTY_TITLE,
    message: TextLibrary.SEARCH_EMPTY_MESSAGE,
  },
  errorMessage: {
    icon: {
      name: 'MdOutlineErrorOutline',
    },
    title: TextLibrary.SEARCH_NO_RESULT_TITLE,
    message: TextLibrary.SEARCH_NO_RESULT_MESSAGE,
  },
})

export const ItemListRecommendedProducts = deepMerge(ItemListDefault, {
  'data-testid': 'recommended-products',
  items: '{{catalog.recommendedProducts}}' as any,
  layout: 'recommended',
  itemCard: {
    ...ItemCardProductRecommended,
    image: {
      ...ItemCardProductRecommended.image,
      dimensions:
        '{{globals.imageConfig.imageSizes.frequentlyBoughtTogetherImage}}' as any,
    },
    counter: {
      ...ItemCardProductRecommended.counter,
      minusButton: {
        ...ItemCardProductRecommended.counter?.minusButton,
        actions: {
          cinnamonActions: [
            {
              name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
              args: {
                ...CounterDefaultArgs,
                referrer: Referrers.FREQUENTLY_BOUGHT_TOGETHER,
              },
            },
          ],
        } as any,
      },
      plusButton: {
        ...ItemCardProductRecommended.counter?.plusButton,
        actions: {
          cinnamonActions: [
            {
              name: CinnamonActionNames.ADD_ITEM_TO_CART,
              args: {
                ...CounterDefaultArgs,
                referrer: Referrers.FREQUENTLY_BOUGHT_TOGETHER,
              },
            },
            {
              name: TemplateActionNames.CART_UPDATE_RECOMMENDED_PRODUCT_BUTTON,
              args: {
                label: TextLibrary.RECOMMENDED_PRODUCTS_PROCEED,
              },
            },
          ],
        } as any,
      },
    },
    body: {
      ...ItemCardProductRecommended.body,
      fields: [
        ItemField.Name,
        ItemField.Description,
        ItemField.PromotionTags,
        ItemField.PromotionTag,
        ItemField.PromotionSavingLabel,
        ItemField.Tags,
      ],
    },
    promotionTag: {
      wrap: true,
      size: 10,
    },
  },
})

export const ItemListPrefillCart = deepMerge(ItemListDefault, {
  'data-testid': 'prefill-cart-items',
  items: '{{cart.prefillCart.items}}' as any,
  layout: 'horizontal',
  itemCard: ItemCardPrefillCart,
})

export const ItemListFilteredProducts = deepMerge(ItemListDefault, {
  layout: '{{globals.catalog.layout}}' as any,
  itemCard: {
    ...ItemCardCatalog,
    image: {
      ...ItemCardCatalog.image,
      dimensions: '{{globals.imageConfig.imageSizes.filterImage}}' as any,
    },
  },
  styles: {
    '& .HeadingWrapper': {
      paddingTop: '0px',
    },
  },
  items: '{{filters.results.productsPrices}}' as any,
  runOnLoad: {
    cinnamonActions: [
      {
        name: CinnamonActionNames.OPEN_MODAL,
        args: {
          container: DefaultModalContainerNames.FILTERS_LIST,
        },
      },
    ],
  },
  status: '{{filters.status}}' as any,
  showEmptyExtraIcon: true,
  actionText: TextLibrary.FILTERS_CLEAR_BUTTON_TEXT,
  actionFunction: {
    cinnamonActions: [
      {
        name: CinnamonActionNames.OPEN_MODAL,
        args: {
          container: DefaultModalContainerNames.FILTERS_LIST,
        },
      },
    ],
  } as any,
  emptyMessage: {
    title: TextLibrary.FILTERS_EMPTY_RESULT,
    message: TextLibrary.FILTERS_EMPTY_RESULT_DESCRIPTION,
  },
})

export const ItemListSuggestedProducts = deepMerge(ItemListDefault, {
  layout: '{{globals.catalog.layout}}' as any,
  items: '{{state.suggestedProductsSlice.suggestedProducts}}' as any,
  status: '{{state.suggestedProductsSlice.status}}' as any,
  itemCard: {
    ...ItemCardCatalog,
    // Re-renders the rows while `refreshCatalog` writes new prices; see
    // `ICatalog.repricing` (COM-1500). ItemList's own memo also yields to it.
    noMemoize: '{{catalog.repricing}}' as any,
    image: {
      ...ItemCardCatalog.image,
      dimensions:
        '{{globals.imageConfig.imageSizes.suggestedItemImage}}' as any,
    },
    counter: {
      ...ItemCardCatalog.counter,
      minusButton: {
        ...ItemCardCatalog.counter?.minusButton,
        actions: {
          cinnamonActions: [
            {
              name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
              args: {
                ...CounterDefaultArgs,
                referrer: 'SUGGESTED_ITEMS',
              },
            },
          ],
        } as any,
      },
      plusButton: {
        ...ItemCardCatalog.counter?.plusButton,
        actions: {
          cinnamonActions: [
            {
              name: CinnamonActionNames.ADD_ITEM_TO_CART,
              args: {
                ...CounterDefaultArgs,
                referrer: Referrers.SUGGESTED_ITEMS,
              },
            },
          ],
        } as any,
      },
    },
  },
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.SUGGESTED_ITEMS_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit:
            '{{env.REACT_APP_RECOMMENDED_ITEMS_ALL_ITEMS_LIMIT}} || {{config.globals.recommendedProducts.allRecommendations}}',
        },
      },
    ],
  },
  emptyMessage: {
    title: TextLibrary.SUGGESTED_ITEMS_EMPTY_RESULT,
    message: TextLibrary.SUGGESTED_ITEMS_EMPTY_RESULT_DESCRIPTION,
  },
  styles: {},
})

/** As `RECOMMENDATION_CAROUSEL_LIMIT_BINDING` in `defaultProps/Carousel.ts`, for the lists. */
const RECOMMENDATION_LIST_LIMIT_BINDING =
  '{{config.globals.recommendedProducts.listLimit}}'

const buildRecommendationItemCard = (referrer: Referrers) => ({
  // Image dimensions intentionally inherit ItemCardCatalog.image — no
  // recommendation-specific image-size global is shipped as a default.
  // Tenants who want a different size override per-placement via SCS
  // `props` instead. The legacy ItemListSuggestedProducts binds to a
  // global named after a tenant concept (suggestedItemImage); productized
  // primitives stay opinion-free until a real signal demands a knob.
  ...ItemCardCatalog,
  counter: {
    ...ItemCardCatalog.counter,
    minusButton: {
      ...ItemCardCatalog.counter?.minusButton,
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.REMOVE_ITEM_FROM_CART,
            args: {
              ...CounterDefaultArgs,
              referrer,
            },
          },
        ],
      } as any,
    },
    plusButton: {
      ...ItemCardCatalog.counter?.plusButton,
      actions: {
        cinnamonActions: [
          {
            name: CinnamonActionNames.ADD_ITEM_TO_CART,
            args: {
              ...CounterDefaultArgs,
              referrer,
            },
          },
        ],
      } as any,
    },
  },
})

/**
 * Generic Ver todos list configs for the productized recommendation pages.
 * Each binds to its slice (`buyAgainSlice` etc.) and triggers the matching
 * fetch action on load. Tenants override visual styling via SCS `props`, and the limit via `globals.recommendedProducts`.
 *
 * `recommendedForYou` ships dark — no SCS config activates the route by
 * default for any tenant.
 */
export const ItemListBuyAgain = deepMerge(ItemListDefault, {
  layout: '{{globals.catalog.layout}}' as any,
  items: '{{state.buyAgainSlice.items}}' as any,
  status: '{{state.buyAgainSlice.status}}' as any,
  itemCard: buildRecommendationItemCard(Referrers.BUY_AGAIN),
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.BUY_AGAIN_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit: RECOMMENDATION_LIST_LIMIT_BINDING,
        },
      },
    ],
  },
  emptyMessage: {
    title: TextLibrary.BUY_AGAIN_EMPTY_RESULT,
    message: TextLibrary.BUY_AGAIN_EMPTY_RESULT_DESCRIPTION,
  },
  styles: {},
})

export const ItemListPreviouslyPurchased = deepMerge(ItemListDefault, {
  layout: '{{globals.catalog.layout}}' as any,
  items: '{{state.previouslyPurchasedSlice.items}}' as any,
  status: '{{state.previouslyPurchasedSlice.status}}' as any,
  itemCard: buildRecommendationItemCard(Referrers.PREVIOUSLY_PURCHASED),
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.PREVIOUSLY_PURCHASED_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit: RECOMMENDATION_LIST_LIMIT_BINDING,
        },
      },
    ],
  },
  emptyMessage: {
    title: TextLibrary.PREVIOUSLY_PURCHASED_EMPTY_RESULT,
    message: TextLibrary.PREVIOUSLY_PURCHASED_EMPTY_RESULT_DESCRIPTION,
  },
  styles: {},
})

export const ItemListRecommendedForYou = deepMerge(ItemListDefault, {
  layout: '{{globals.catalog.layout}}' as any,
  items: '{{state.recommendedForYouSlice.items}}' as any,
  status: '{{state.recommendedForYouSlice.status}}' as any,
  itemCard: buildRecommendationItemCard(Referrers.RECOMMENDED_FOR_YOU),
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.RECOMMENDED_FOR_YOU_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit: RECOMMENDATION_LIST_LIMIT_BINDING,
        },
      },
    ],
  },
  emptyMessage: {
    title: TextLibrary.RECOMMENDED_FOR_YOU_EMPTY_RESULT,
    message: TextLibrary.RECOMMENDED_FOR_YOU_EMPTY_RESULT_DESCRIPTION,
  },
  styles: {},
})

export const ItemListChosenFreegoodsQuantity = deepMerge(ItemListDefault, {
  layout: '{{globals.catalog.layout}}' as any,
  items: '{{catalog.freegoodsConf.quantitySelection.products}}' as any,
  itemCard: ItemCardFreegoodsQuantitySelection,
  maxValidItems: '{{catalog.freegoodsConf.maxQuantity}}' as any,
})

export const ItemListLinkedProducts: DefaultProps<ItemListProps> = {
  layout: 'horizontal',
  items: '{{cart.linkedProducts.items}}' as any,
  itemCard: ItemCardLinkedProducts,
}

export const ItemListCategoryNav = deepMerge(ItemListDefault, {
  styles: {
    '& .HeadingWrapper': {
      paddingTop: '0px',
    },
  },
  layout: '{{globals.catalog.layout}}' as any,
  itemCard: {
    ...ItemCardCatalog,
    image: {
      ...ItemCardCatalog.image,
      dimensions: '{{globals.imageConfig.imageSizes.catalogueImage}}' as any,
    },
  },
  items: '{{categoriesNav.category.productsPrices}}' as any,
  heading: {
    value: '{{categoriesNav.category.category}}',
  },
  status: '{{categoriesNav.categoryStatus}}' as any,
  emptyMessage: {
    icon: {
      name: 'ErrorOutline',
    },
    title: TextLibrary.EMPTY_CATEGORY_TITLE,
    message: TextLibrary.EMPTY_CATEGORY_MESSAGE,
    buttons: [
      {
        label: `${TextLibrary.EMPTY_CATEGORY_BACK_BUTTON}`,
        actions: {
          cinnamonActions: [
            {
              name: CinnamonActionNames.GO_BACK,
              args: {
                path: `${CinnamonPaths.CATEGORIES_NAVIGATION}`,
              },
            },
          ],
        } as any,
      },
    ],
  },
})
